import{useCallback,useEffect,useMemo,useRef,useState}from"react";
import{
  ConfirmAction,
  DangerAction,
  DetailPanel,
  FilterBar,
  PageAction,
  ResourceList,
  ResourceTable,
  SearchField,
  SectionHeader,
  StateText,
  WorkspaceHeader,
}from"../../components/Foundation";
import{EmptyState,InlineNotice,LoadingLine}from"../../components/State";
import{IdentityAccessTranslate}from"./IdentityAccessTranslate";
import"./identityAccessV2.css";
import{useAppI18n}from"../../lib/appI18n";
import{SecurityIdentitySummary}from"./SecurityIdentitySummary";
// IAM_COMPACT_I18N_V2
import{securityApi}from"../../lib/api";
import{
  identityAccessApi,
  type ControlPlane,
  type Effect,
  type LoginProfile,
  type PrincipalDetail,
  type PrincipalSummary,
}from"../../lib/identityAccessApi";
import{unlockSecurityAuthority}from"../../lib/securityAuthority";
import{access}from"../../lib/normalize";
import{fmtDate}from"../../lib/format";
import{useResource}from"../../lib/useResource";

type MutationPhase=
  |"waiting_passkey"
  |"applying"
  |"success"
  |"error";

type MutationFeedback={
  key:string;
  phase:MutationPhase;
  title:string;
  detail:string;
}|null;

const EFFECTS:Effect[]=[
  "ALLOW",
  "DENY",
  "STEP_UP",
  "APPROVAL_REQUIRED",
  "DUAL_APPROVAL_REQUIRED",
];

function humanError(error:string){
  if(/authentication_required|passkey_session_required/i.test(error)){
    return{
      title:"Passkey session required",
      detail:"Sign in with an approved passkey, then reopen Identity & Access.",
    };
  }

  if(/authority_step_up_required|permission_denied|access_management_denied/i.test(error)){
    return{
      title:"Administrative authority required",
      detail:"Unlock Security Authority to manage organization access.",
    };
  }

  return{
    title:"Identity & Access could not load",
    detail:error,
  };
}

function AccessBoundary({
  error,
  onUnlocked,
}:{
  error:string|null;
  onUnlocked:()=>void|Promise<void>;
}){
  const[busy,setBusy]=useState(false);
  const[actionError,setActionError]=useState<string|null>(null);

  if(!error)return null;

  const info=humanError(actionError||error);
  const sessionRequired=
    /authentication_required|passkey_session_required/i.test(error);

  const unlock=async()=>{
    setBusy(true);
    setActionError(null);

    try{
      await unlockSecurityAuthority();
      await onUnlocked();
    }catch(e){
      setActionError(
        e instanceof Error
          ?e.message
          :"Security Authority unlock failed.",
      );
    }finally{
      setBusy(false);
    }
  };

  return <InlineNotice
    tone="attention"
    title={info.title}
    detail={info.detail}
    action={
      sessionRequired
        ?undefined
        :<PageAction
          disabled={busy}
          onClick={()=>void unlock()}
        >
          {busy?"Waiting for passkey…":"Unlock Security Authority"}
        </PageAction>
    }
  />;
}

function sourceText(source:Record<string,unknown>){
  const type=String(source.type??"SOURCE");
  const key=source.key??source.effect??source.id??"";
  return key?`${type}: ${String(key)}`:type;
}

function defaultJitExpiry(){
  const d=new Date(Date.now()+60*60*1000);
  const pad=(n:number)=>String(n).padStart(2,"0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function IdentityAccessWorkspace({initialView="access",onBusyChange}:{initialView?:"access"|"assignments"|"grant"|"password"|"capability";onBusyChange?:(busy:boolean)=>void}={}){
  const{t}=useAppI18n();
  const legacy=useResource(useCallback(securityApi.access,[]));
  const cp=useResource(useCallback(identityAccessApi.controlPlane,[]));

  const control=(cp.data as {control_plane?:ControlPlane}|null)?.control_plane??null;
  const legacyRows=useMemo(()=>access(legacy.data),[legacy.data]);

  const[query,setQuery]=useState("");
  const[selectedId,setSelectedId]=useState<string|null>(null);
  const[detail,setDetail]=useState<PrincipalDetail|null>(null);
  const[detailBusy,setDetailBusy]=useState(false);
  const[detailError,setDetailError]=useState<string|null>(null);
  const[mutationFeedback,setMutationFeedback]=useState<MutationFeedback>(null);
  const[reason,setReason]=useState("");
  const[roleKey,setRoleKey]=useState("");
  const[packageKey,setPackageKey]=useState("");
  const[capability,setCapability]=useState("");
  const[effect,setEffect]=useState<Effect>("DENY");
  const[jitExpiry,setJitExpiry]=useState(defaultJitExpiry());
  const[profileKey,setProfileKey]=useState("");
  const[profileDraft,setProfileDraft]=useState<LoginProfile|null>(null);
  const[passwordStatus,setPasswordStatus]=useState<'NOT_CONFIGURED'|'ACTIVE'|'DISABLED'>('NOT_CONFIGURED');
  const[isLoadingPassword,setIsLoadingPassword]=useState(false);
  const[passwordForm,setPasswordForm]=useState({password:'',confirm:''});

  const principals=useMemo(()=>{
    const all=control?.principals??[];
    const q=query.trim().toLowerCase();

    if(!q)return all;

    return all.filter(p=>
      [
        p.display_name,
        p.type,
        p.status,
        ...(p.roles??[]),
        ...(p.packages??[]),
        ...(p.identities??[]).map(x=>x.email??""),
      ].some(value=>String(value).toLowerCase().includes(q)),
    );
  },[control,query]);

  useEffect(()=>{
    if(!control)return;

    if(!selectedId&&control.principals.length){
      setSelectedId(control.principals[0].id);
    }

    if(!capability&&control.capabilities.length)setCapability(control.capabilities[0].capability_key);
    if(!profileKey&&control.login_profiles.length)setProfileKey(control.login_profiles[0].profile_key);
  },[control,selectedId,capability,profileKey]);

  const detailGeneration=useRef(0);
  const loadDetail=useCallback(async(id:string)=>{
    const generation=++detailGeneration.current;
    setSelectedId(id);
    setDetailBusy(true);
    setDetail(null);
    setDetailError(null);

    try{
      const result=await identityAccessApi.principal(id,3);
      if(generation!==detailGeneration.current)return;
      setDetail(result.principal);
    }catch(e){
      if(generation!==detailGeneration.current)return;
      setDetail(null);
      setDetailError(
        e instanceof Error
          ?e.message
          :"principal_detail_failed",
      );
    }finally{
      if(generation===detailGeneration.current)setDetailBusy(false);
    }
  },[]);

  useEffect(()=>{
    if(selectedId)void loadDetail(selectedId);
  },[selectedId,loadDetail]);

  useEffect(()=>{
    if(!control||!profileKey)return;
    const p=control.login_profiles.find(x=>x.profile_key===profileKey)??null;
    setProfileDraft(p?{...p,methods:p.methods.map(m=>({...m}))}:null);
  },[control,profileKey]);

  async function refreshAll(){
    await Promise.all([cp.refresh(),legacy.refresh()]);
    if(selectedId)await loadDetail(selectedId);
  }

  const mutationBusy=
    mutationFeedback?.phase==="waiting_passkey"
    ||mutationFeedback?.phase==="applying";

  useEffect(()=>{onBusyChange?.(mutationBusy);return()=>onBusyChange?.(false);},[mutationBusy,onBusyChange]);

  async function mutate(
    key:string,
    title:string,
    fn:(hooks:{
      onPasskey:()=>void;
      onApplying:()=>void;
    })=>Promise<unknown>,
  ){
    if(mutationBusy)return;

    if(reason.trim().length<5){
      setMutationFeedback({
        key,
        phase:"error",
        title:"Change reason required",
        detail:"Enter at least five characters explaining why this access change is needed.",
      });
      return;
    }

    try{
      await fn({
        onPasskey:()=>setMutationFeedback({
          key,
          phase:"waiting_passkey",
          title:"Waiting for passkey…",
          detail:"Complete the fresh passkey ceremony to authorize this change.",
        }),
        onApplying:()=>setMutationFeedback({
          key,
          phase:"applying",
          title:"Applying…",
          detail:"Passkey verified. The governed IAM mutation is being applied.",
        }),
      });

      setMutationFeedback({
        key,
        phase:"success",
        title,
        detail:"Applied and recorded in the IAM audit trail.",
      });

      await refreshAll();
    }catch(e){
      const detail=
        e instanceof Error
          ?e.message
          :"The requested change was not applied.";

      setMutationFeedback({
        key,
        phase:"error",
        title:`${title} failed`,
        detail,
      });

      if(/authority_step_up_required/i.test(detail)&&selectedId){
        await loadDetail(selectedId);
      }
    }
  }

  function getPasswordStatusSelectedUser():void{
    if(!selectedId)return;
    setIsLoadingPassword(true);
    identityAccessApi.getPasswordStatus(selectedId).then((statusResponse:any)=>{
      setPasswordStatus(statusResponse.status||'NOT_CONFIGURED');
      setIsLoadingPassword(false);
    }).catch((e:any)=>{
      setPasswordStatus('NOT_CONFIGURED');
      setIsLoadingPassword(false);
      setMutationFeedback({
        key:'get-password',
        phase:'error',
        title:'Failed to get password status',
        detail:e instanceof Error ? e.message : 'Unknown error',
      });
    });
  }

  async function setPasswordSelectedUser():Promise<void>{
    if(!selectedId)return;
    if(passwordForm.password.length<12){
      setMutationFeedback({
        key:'set-password',
        phase:'error',
        title:'Password too short',
        detail:'Password must be at least 12 characters.',
      });
      return;
    }
    if(passwordForm.password!==passwordForm.confirm){
      setMutationFeedback({
        key:'set-password',
        phase:'error',
        title:'Password mismatch',
        detail:'Passwords do not match.',
      });
      return;
    }
    setIsLoadingPassword(true);
    setMutationFeedback({
      key:'set-password',
      phase:'waiting_passkey',
      title:'Waiting for passkey…',
      detail:'Complete the fresh passkey ceremony to authorize password change.',
    });
    setMutationFeedback({
      key:'set-password',
      phase:'applying',
      title:'Applying…',
      detail:'Passkey verified. The password mutation is being applied.',
    });
    try{
      const result = await identityAccessApi.setPassword(selectedId,passwordForm.password,{
        onPasskey:()=>setMutationFeedback({
          key:'set-password',
          phase:'waiting_passkey',
          title:'Waiting for passkey…',
          detail:'Complete the fresh passkey ceremony to authorize password change.',
        }),
        onApplying:()=>setMutationFeedback({
          key:'set-password',
          phase:'applying',
          title:'Applying…',
          detail:'Passkey verified. The password mutation is being applied.',
        }),
      });
      setMutationFeedback({
        key:'set-password',
        phase:'success',
        title:'Password reset',
        detail:'Password credential reset and recorded in the IAM audit trail.',
      });
      await getPasswordStatusSelectedUser();
      setPasswordForm({password:'',confirm:''});
    }catch(e:any){
      setMutationFeedback({
        key:'set-password',
        phase:'error',
        title:'Password reset failed',
        detail:e instanceof Error ? e.message : 'Unknown error',
      });
    }finally{
      setIsLoadingPassword(false);
    }
  }

  async function disablePasswordSelectedUser():Promise<void>{
    if(!selectedId)return;
    setIsLoadingPassword(true);
    setMutationFeedback({
      key:'disable-password',
      phase:'waiting_passkey',
      title:'Waiting for passkey…',
      detail:'Complete the fresh passkey ceremony to authorize password disable.',
    });
    setMutationFeedback({
      key:'disable-password',
      phase:'applying',
      title:'Applying…',
      detail:'Passkey verified. The password disable mutation is being applied.',
    });
    try{
      const result = await identityAccessApi.disablePassword(selectedId,{
        onPasskey:()=>setMutationFeedback({
          key:'disable-password',
          phase:'waiting_passkey',
          title:'Waiting for passkey…',
          detail:'Complete the fresh passkey ceremony to authorize password disable.',
        }),
        onApplying:()=>setMutationFeedback({
          key:'disable-password',
          phase:'applying',
          title:'Applying…',
          detail:'Passkey verified. The password disable mutation is being applied.',
        }),
      });
      if(result.success){
        setMutationFeedback({
          key:'disable-password',
          phase:'success',
          title:'Password disabled',
          detail:'Password credential disabled and recorded in the IAM audit trail.',
        });
        await getPasswordStatusSelectedUser();
      }else{
        setMutationFeedback({
          key:'disable-password',
          phase:'error',
          title:'Password disable failed',
          detail:result.status||'Unknown error',
        });
      }
    }catch(e:any){
      setMutationFeedback({
        key:'disable-password',
        phase:'error',
        title:'Password disable failed',
        detail:e instanceof Error ? e.message : 'Unknown error',
      });
    }finally{
      setIsLoadingPassword(false);
    }
  }

  function mutationNotice(...keys:string[]){
    if(!mutationFeedback||!keys.includes(mutationFeedback.key)){
      return null;
    }

    return <div className="iam-action-status">
      <InlineNotice
        tone={
          mutationFeedback.phase==="error"
            ?"critical"
            :mutationFeedback.phase==="success"
              ?"neutral"
              :"attention"
        }
        title={mutationFeedback.title}
        detail={mutationFeedback.detail}
      />
    </div>;
  }

  const cpError=
    cp.status==="error"&&!cp.data
      ?cp.error
      :null;

  const legacyError=
    legacy.status==="error"&&!legacy.data
      ?legacy.error
      :null;

  const selected:PrincipalSummary|null=
    control?.principals.find(x=>x.id===selectedId)??null;

  const profiles=control?.login_profiles??[];
  const[view,setView]=useState<"overview"|"principals"|"profiles"|"activity">("principals");
  const[principalView,setPrincipalView]=useState<"access"|"assignments"|"grant"|"password"|"capability">(initialView);
  // KNSAIX_SECURITY_UX_V3_TASK_INTENT_P2
  useEffect(()=>{
    const applyIntent=()=>{
      try{
        const raw=sessionStorage.getItem("knsaix.security.taskIntent.v1");
        if(!raw)return;
        const intent=JSON.parse(raw) as {action?:string;createdAt?:number};
        if(intent.createdAt&&Date.now()-intent.createdAt>10*60*1000){
          sessionStorage.removeItem("knsaix.security.taskIntent.v1");
          return;
        }
        if(intent.action==="grant-access"){
          setView("principals");
          setPrincipalView("grant");
        }else if(intent.action==="temporary-access"){
          setView("principals");
          setPrincipalView("capability");
        }
      }catch{
        sessionStorage.removeItem("knsaix.security.taskIntent.v1");
      }
    };
    applyIntent();
    const listener=()=>applyIntent();
    window.addEventListener("knsaix:security-task-intent",listener);
    return()=>window.removeEventListener("knsaix:security-task-intent",listener);
  },[]);

  useEffect(()=>{
    const raw=sessionStorage.getItem("knsaix.security.taskIntent.v1");
    let keepTaskView=false;
    try{
      const intent=raw?JSON.parse(raw) as {action?:string}:null;
      keepTaskView=intent?.action==="grant-access"||intent?.action==="temporary-access";
    }catch{}
    if(!keepTaskView)setPrincipalView(initialView);
  },[selectedId,initialView]);

  return <IdentityAccessTranslate><div className={`page iam-workspace kns-os-page iam-view-${view}`}>
    <WorkspaceHeader
      eyebrow="Security Administration"
      title="Identity & Access"
      description="Canonical identities, effective access, temporary privilege and login policy in one governed workspace."
      actions={
        cp.status==="loading"||legacy.status==="loading"
          ?<LoadingLine/>
          :<PageAction onClick={()=>void refreshAll()}>
            Refresh
          </PageAction>
      }
    />

    <details className="os-governance-note"><summary>{t("sc.technicalAccess")}</summary>
    <InlineNotice
      tone="neutral"
      title="Authority stays separate from role"
      detail="Authentication proves identity. Roles and packages grant resource permissions. Governance authority remains independently controlled and every sensitive mutation requires a fresh passkey."
    />

    </details>
    <AccessBoundary error={cpError||legacyError} onUnlocked={refreshAll}/>
    <nav className="iam-view-tabs" aria-label="Identity & Access sections">
      <button type="button" aria-current={view==="overview"?"page":undefined} onClick={()=>setView("overview")}>Overview</button>
      <button type="button" aria-current={view==="principals"?"page":undefined} onClick={()=>setView("principals")}>Principals <span className="iam-tab-count">{control?.principals.length??0}</span></button>
      <button type="button" aria-current={view==="profiles"?"page":undefined} onClick={()=>setView("profiles")}>Login profiles <span className="iam-tab-count">{profiles.length}</span></button>
      <button type="button" aria-current={view==="activity"?"page":undefined} onClick={()=>setView("activity")}>Recent access activity <span className="iam-tab-count">{control?.recent_events?.length??0}</span></button>
    </nav>

    {legacyRows.length>0&&view==="overview"&&
      <section className="iam-section">
        <SectionHeader
          title="Current organization access"
          description="Existing static, temporary and effective system access remains visible while IAM policy is managed below."
        />

        <ResourceTable label="Current organization access">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Static</th>
                <th>JIT</th>
                <th>Effective</th>
                <th>System key</th>
                <th>JIT expiry</th>
              </tr>
            </thead>
            <tbody>
              {legacyRows.map((x,i)=>
                <tr key={x.subject_id||`${x.subject_name}-${i}`}>
                  <td><strong>{x.subject_name}</strong></td>
                  <td>{x.organization_role}</td>
                  <td>{x.static_system_level}</td>
                  <td>{x.active_jit_level||"—"}</td>
                  <td>{x.effective_system_level}</td>
                  <td>
                    <StateText tone={x.system_key_ready?"positive":"neutral"}>
                      {x.system_key_ready?"READY":"NONE"}
                    </StateText>
                  </td>
                  <td>{fmtDate(x.active_jit_expires_at)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </ResourceTable>
      </section>
    }

    {control&&
      <>
        {view==="principals"&&<section className="iam-section">
          <SectionHeader
            title="Principals"
            description="Select a canonical principal to inspect effective access and its exact sources."
          />

          <FilterBar label="Principal discovery">
            <SearchField
              label="Search principals"
              value={query}
              onChange={e=>setQuery(e.target.value)}
              placeholder="Name, identity, role or package"
            />
          </FilterBar>

          <div className="iam-list-detail iam-principal-workspace">
            <ResourceList label="Canonical principals">
              <div className="kns-os-principal-cards">
               {principals.map(p=>{const email=(p.identities??[]).find(identity=>identity.email)?.email??"";const initials=p.display_name.trim().split(/\s+/).map(x=>x[0]).join("").slice(0,2).toUpperCase()||"ID";return <button type="button" key={p.id} className={`kns-os-person-card ${selectedId===p.id?"selected":""}`} aria-pressed={selectedId===p.id} disabled={mutationBusy} onClick={()=>setSelectedId(p.id)}>
                <span className="kns-os-person-avatar" aria-hidden="true">{initials}</span>
                <span className="kns-os-person-main"><strong>{p.display_name}</strong><small>{email||p.type}</small><span className="kns-os-person-chips">{(p.roles??[]).slice(0,2).map(role=><i key={role}>{role}</i>)}{(p.packages??[]).slice(0,1).map(pkg=><i key={pkg}>{pkg}</i>)}</span></span>
                <span className="kns-os-person-state"><StateText tone={p.status==="ACTIVE"?"positive":"neutral"}>{p.status}</StateText><small>{(p.roles??[]).length} {t("sc.roles")} · {(p.packages??[]).length} {t("sc.packages")}</small></span>
                <span className="kns-os-chevron" aria-hidden="true">›</span>
               </button>;})}
              </div>
            </ResourceList>

            <DetailPanel
              title={selected?.display_name??"Principal detail"}
              actions={detailBusy?<LoadingLine/>:undefined}
            >
              {!selected&&
                <EmptyState
                  title="Select a principal"
                  detail="Effective access, assignments and temporary grants appear here."
                />
              }

              {selected&&detailError&&
                <AccessBoundary
                  error={detailError}
                  onUnlocked={()=>loadDetail(selected.id)}
                />
              }

              {selected&&!detailError&&detail&&detail.principal.id===selectedId&&
                <div className="iam-detail-stack">
                  <SecurityIdentitySummary key={selected.id} principal={selected} detail={detail} onNavigate={setPrincipalView} editor={{
                    reason,onReason:setReason,busy:mutationBusy||cp.status!=="success",roles:control.roles??[],
                    feedback:mutationNotice("inline-policy","inline-role","inline-role-remove"),
                    onPolicy:(key,value)=>void mutate("inline-policy","Capability override applied",hooks=>identityAccessApi.setOverride(selected.id,key,value,reason,null,hooks)),
                    onAssignRole:key=>void mutate("inline-role","Role assigned",hooks=>identityAccessApi.assignRole(selected.id,key,reason,null,hooks)),
                    onRemoveRole:id=>void mutate("inline-role-remove","Role revoked",hooks=>identityAccessApi.revokeRole(id,reason,hooks)),
                  }}/>
                  <details className="os-advanced-actions"><summary>{t("sc.technicalAccess")}</summary>
                  <nav className="iam-detail-tabs" aria-label="Principal access sections">
                    <button type="button" aria-current={principalView==="access"?"page":undefined} onClick={()=>setPrincipalView("access")}>Effective access</button>
                    <button type="button" aria-current={principalView==="assignments"?"page":undefined} onClick={()=>setPrincipalView("assignments")}>Assignments</button>
                    <button type="button" aria-current={principalView==="grant"?"page":undefined} onClick={()=>setPrincipalView("grant")}>Grant access</button>
                    <button type="button" aria-current={principalView==="password"?"page":undefined} onClick={()=>setPrincipalView("password")}>Password</button>
                    <button type="button" aria-current={principalView==="capability"?"page":undefined} onClick={()=>setPrincipalView("capability")}>Capability policy</button>
                  </nav>
                  </details>
                  {principalView==="access"&&<div>
                    <SectionHeader
                      title="Effective Access / Why"
                      description="The decision and the role, package, override or JIT source that contributed to it."
                    />

                    {detail.effective_access.length?
                      <details className="scv4-tech-disclosure">
                       <summary>{t("sc.technicalAccess")} ({detail.effective_access.length})</summary>
                       <ResourceTable label={`Effective access for ${selected.display_name}`}>
                        <table className="admin-table compact">
                          <thead>
                            <tr>
                              <th>Capability</th>
                              <th>Decision</th>
                              <th>Why</th>
                              <th>Sources</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.effective_access.map(row=>
                              <tr key={row.capability}>
                                <td><strong>{row.capability}</strong></td>
                                <td>
                                  <StateText
                                    tone={
                                      row.decision==="DENY"
                                        ?"critical"
                                        :row.decision==="ALLOW"
                                          ?"positive"
                                          :"attention"
                                    }
                                  >
                                    {row.decision}
                                  </StateText>
                                </td>
                                <td>{row.reason}</td>
                                <td>
                                  {row.sources.length
                                    ?row.sources.map(sourceText).join(", ")
                                    :"—"}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                       </ResourceTable>
                      </details>
                      :<EmptyState
                        title="No effective capabilities"
                        detail="This principal currently resolves to no effective capability rows."
                      />
                    }
                  </div>}

                  {principalView!=="access"&&<div className="iam-change-context">
                    <SectionHeader
                      title="Change context"
                      description="One explicit reason is reused for the next governed mutation and recorded in the IAM audit trail."
                    />
                    <label className="iam-reason">
                      <span>Change reason</span>
                      <textarea
                        value={reason}
                        onChange={e=>setReason(e.target.value)}
                        maxLength={1000}
                        placeholder="Why is this access change needed?"
                      />
                    </label>
                  </div>}

                  {principalView==="assignments"&&<div>
                    <SectionHeader
                      title="Assignments"
                      description="Role and access-package membership. Removing an assignment is an explicit destructive action."
                    />

                    <ResourceList label="Principal assignments">
                      <div className="iam-resource-list">
                        {detail.role_assignments.map(a=>
                          <div className="iam-resource-row" key={a.assignment_id}>
                            <div>
                              <strong>Role · {a.role_key}</strong>
                              <span>Expires {fmtDate(a.expires_at)}</span>
                            </div>
                            <DangerAction
                              disabled={mutationBusy}
                              onClick={()=>void mutate(
                                "role-revoke",
                                "Role revoked",
                                hooks=>identityAccessApi.revokeRole(
                                  a.assignment_id,
                                  reason,
                                  hooks,
                                ),
                              )}
                            >
                              Revoke role
                            </DangerAction>
                          </div>
                        )}

                        {detail.package_assignments.map(a=>
                          <div className="iam-resource-row" key={a.assignment_id}>
                            <div>
                              <strong>Package · {a.package_key}</strong>
                              <span>Expires {fmtDate(a.expires_at)}</span>
                            </div>
                            <DangerAction
                              disabled={mutationBusy}
                              onClick={()=>void mutate(
                                "package-revoke",
                                "Package revoked",
                                hooks=>identityAccessApi.revokePackage(
                                  a.assignment_id,
                                  reason,
                                  hooks,
                                ),
                              )}
                            >
                              Revoke package
                            </DangerAction>
                          </div>
                        )}

                        {!detail.role_assignments.length&&!detail.package_assignments.length&&
                          <EmptyState
                            title="No active assignments"
                            detail="No role or access package is currently assigned."
                          />
                        }
                      </div>
                    </ResourceList>
                  </div>}

                  {principalView==="grant"&&<div className="sux3-task-target" data-task-target="grant-access">
                    <SectionHeader
                      title="Grant access"
                      description="New role and package grants require a reason and a fresh passkey."
                    />

                    <div className="iam-action-pairs">
                      <div className="iam-action-pair">
                        <label>
                          <span>Role</span>
                          <select value={roleKey} onChange={e=>setRoleKey(e.target.value)}>
                            <option value="">Select a role…</option>
                            {(control.roles??[]).map(r=>
                              <option key={r.role_key} value={r.role_key}>
                                {r.display_name} · {r.class}
                              </option>
                            )}
                          </select>
                        </label>

                        <ConfirmAction
                          disabled={!roleKey||mutationBusy}
                          onClick={()=>void mutate(
                            "role-assign",
                            "Role assigned",
                            hooks=>identityAccessApi.assignRole(
                              selected.id,
                              roleKey,
                              reason,
                              null,
                              hooks,
                            ),
                          )}
                        >
                          Assign role
                        </ConfirmAction>
                      </div>

                      <div className="iam-action-pair">
                        <label>
                          <span>Access package</span>
                          <select value={packageKey} onChange={e=>setPackageKey(e.target.value)}>
                            <option value="">Select a package…</option>
                            {(control.packages??[]).map(p=>
                              <option key={p.package_key} value={p.package_key}>
                                {p.display_name}
                              </option>
                            )}
                          </select>
                        </label>

                        <ConfirmAction
                          disabled={!packageKey||mutationBusy}
                          onClick={()=>void mutate(
                            "package-assign",
                            "Package assigned",
                            hooks=>identityAccessApi.assignPackage(
                              selected.id,
                              packageKey,
                              reason,
                              null,
                              hooks,
                            ),
                          )}
                        >
                          Assign package
                        </ConfirmAction>
                      </div>
                    </div>
                    {mutationNotice(
                      "role-assign",
                      "package-assign",
                      "role-revoke",
                      "package-revoke",
                    )}
                  </div>}

                  {principalView==="password"&&<div className="iam-section">
                    <SectionHeader
                      title="Password Credential"
                      description="Password management for the selected user. Requires fresh passkey approval for mutations."
                    />
                    <div className="password-credential-status">
                      <StateText
                        tone={
                          passwordStatus==="ACTIVE"
                            ?"positive"
                            :passwordStatus==="DISABLED"
                              ?"critical"
                              :"neutral"
                        }
                      >
                        {passwordStatus}
                      </StateText>
                    </div>
                    <div className="password-actions">
                      <div className="password-form-preview password-form-editor">
                          <label><span>Password</span><input type="password" value={passwordForm.password} onChange={e=>setPasswordForm({...passwordForm,password:e.target.value})} placeholder="Enter new password" minLength={12} required/></label>
                          <label><span>Confirm password</span><input type="password" value={passwordForm.confirm} onChange={e=>setPasswordForm({...passwordForm,confirm:e.target.value})} placeholder="Confirm new password" minLength={12} required/></label>
                        </div>
                      <ConfirmAction
                        disabled={isLoadingPassword||!passwordForm.password||passwordForm.password!==passwordForm.confirm}
                        onClick={isLoadingPassword?void null:async()=>{
                          await setPasswordSelectedUser();
                        }}
                        title="Reset password"
                      >
                        Reset password
                      </ConfirmAction>
                      <ConfirmAction
                        disabled={isLoadingPassword}
                        onClick={isLoadingPassword?void null:async()=>{
                          await disablePasswordSelectedUser();
                        }}
                        title="Disable password"
                      >
                        Disable password
                      </ConfirmAction>
                    </div>
                  </div>}

                  {principalView==="capability"&&<div className="sux3-task-target" data-task-target="temporary-access">
                    <SectionHeader
                      title="Capability policy"
                      description="Direct overrides and short-lived JIT grants remain explicit, attributable and revocable."
                    />

                    <div className="kns-os-preset-row" aria-label={t("sc.temporaryAccess")}>
                      <button type="button" onClick={()=>{const d=new Date(Date.now()+15*60*1000);const pad=(n:number)=>String(n).padStart(2,"0");setJitExpiry(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);}}>{t("sc.duration15m")}</button>
                      <button type="button" onClick={()=>setJitExpiry(defaultJitExpiry())}>{t("sc.duration1h")}</button>
                      <button type="button" onClick={()=>{const d=new Date();d.setHours(23,59,0,0);const pad=(n:number)=>String(n).padStart(2,"0");setJitExpiry(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);}}>{t("sc.durationToday")}</button>
                    </div>

                    <div className="iam-policy-fields">
                      <label>
                        <span>Capability</span>
                        <select value={capability} onChange={e=>setCapability(e.target.value)}>
                          {(control.capabilities??[]).map(c=>
                            <option key={c.capability_key} value={c.capability_key}>
                              {c.capability_key} · {c.risk_class}
                            </option>
                          )}
                        </select>
                      </label>

                      <label>
                        <span>Decision</span>
                        <select value={effect} onChange={e=>setEffect(e.target.value as Effect)}>
                          {EFFECTS.map(value=>
                            <option key={value} value={value}>{value}</option>
                          )}
                        </select>
                      </label>

                      <label>
                        <span>JIT expiry</span>
                        <input
                          type="datetime-local"
                          value={jitExpiry}
                          onChange={e=>setJitExpiry(e.target.value)}
                        />
                      </label>
                    </div>

                    <div className="iam-action-row">
                      <ConfirmAction
                        disabled={!capability||mutationBusy}
                        onClick={()=>void mutate(
                          "override-set",
                          "Capability override applied",
                          hooks=>identityAccessApi.setOverride(
                            selected.id,
                            capability,
                            effect,
                            reason,
                            null,
                            hooks,
                          ),
                        )}
                      >
                        Apply override
                      </ConfirmAction>

                      <ConfirmAction
                        disabled={!capability||!jitExpiry||mutationBusy}
                        onClick={()=>void mutate(
                          "jit-assign",
                          "Temporary access granted",
                          hooks=>identityAccessApi.assignJit(
                            selected.id,
                            capability,
                            effect,
                            new Date(jitExpiry).toISOString(),
                            reason,
                            hooks,
                          ),
                        )}
                      >
                        Grant temporary access
                      </ConfirmAction>
                    </div>

                    {mutationNotice(
                      "override-set",
                      "override-revoke",
                      "jit-assign",
                      "jit-revoke",
                    )}

                    {(detail.overrides.length>0||detail.jit.length>0)&&
                      <ResourceList label="Overrides and temporary grants">
                        <div className="iam-resource-list">
                          {detail.overrides.map(o=>
                            <div className="iam-resource-row" key={o.override_id}>
                              <div>
                                <strong>Override · {o.capability}</strong>
                                <span>{o.effect} · {o.reason}</span>
                              </div>
                              <DangerAction
                                disabled={mutationBusy}
                                onClick={()=>void mutate(
                                  "override-revoke",
                                  "Capability override revoked",
                                  hooks=>identityAccessApi.revokeOverride(
                                    o.override_id,
                                    reason,
                                    hooks,
                                  ),
                                )}
                              >
                                Revoke override
                              </DangerAction>
                            </div>
                          )}

                          {detail.jit.map(j=>
                            <div className="iam-resource-row" key={j.grant_id}>
                              <div>
                                <strong>JIT · {j.capability}</strong>
                                <span>{j.effect} · expires {fmtDate(j.expires_at)}</span>
                              </div>
                              <DangerAction
                                disabled={mutationBusy}
                                onClick={()=>void mutate(
                                  "jit-revoke",
                                  "Temporary access revoked",
                                  hooks=>identityAccessApi.revokeJit(
                                    j.grant_id,
                                    reason,
                                    hooks,
                                  ),
                                )}
                              >
                                Revoke temporary access
                              </DangerAction>
                            </div>
                          )}
                        </div>
                      </ResourceList>
                    }
                  </div>}

                </div>
              }
            </DetailPanel>
          </div>
        </section>}

        {view==="profiles"&&<section className="iam-section">
          <SectionHeader
            title="Login profiles"
            description="Authentication methods, assurance and self-registration policy. Planned identity providers remain unavailable until their implementation is LIVE."
          />

          <div className="iam-list-detail iam-profile-workspace">
            <ResourceTable label="Login profiles">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Audience</th>
                    <th>Assurance</th>
                    <th>Self registration</th>
                    <th>Default role</th>
                    <th>Version</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map(p=>
                    <tr
                      key={p.profile_key}
                      className={profileKey===p.profile_key?"iam-selected-row":undefined}
                    >
                      <td>
                        <button
                          className="iam-resource-link"
                          type="button"
                          aria-pressed={profileKey===p.profile_key}
                          onClick={()=>setProfileKey(p.profile_key)}
                        >
                          {p.display_name}
                        </button>
                      </td>
                      <td>{p.audience}</td>
                      <td>AAL {p.min_assurance}</td>
                      <td>{p.self_registration?"YES":"NO"}</td>
                      <td>{p.default_role_key||"—"}</td>
                      <td>v{p.version}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ResourceTable>

            <DetailPanel title={profileDraft?.display_name??"Login profile"}>
              {profileDraft?
                <div className="iam-detail-stack">
                  <div className="iam-form-grid">
                    <label>
                      <span>Minimum assurance</span>
                      <input
                        type="number"
                        min={1}
                        max={3}
                        value={profileDraft.min_assurance}
                        onChange={e=>setProfileDraft({
                          ...profileDraft,
                          min_assurance:Number(e.target.value),
                        })}
                      />
                    </label>

                    <label>
                      <span>Session max seconds</span>
                      <input
                        type="number"
                        min={300}
                        max={2592000}
                        value={profileDraft.session_max_age_seconds}
                        onChange={e=>setProfileDraft({
                          ...profileDraft,
                          session_max_age_seconds:Number(e.target.value),
                        })}
                      />
                    </label>

                    <label>
                      <span>Default role</span>
                      <select
                        value={profileDraft.default_role_key??""}
                        onChange={e=>setProfileDraft({
                          ...profileDraft,
                          default_role_key:e.target.value||null,
                        })}
                      >
                        <option value="">No default role</option>
                        {(control.roles??[]).map(r=>
                          <option key={r.role_key} value={r.role_key}>
                            {r.display_name}
                          </option>
                        )}
                      </select>
                    </label>

                    <label className="iam-check">
                      <input
                        type="checkbox"
                        checked={profileDraft.self_registration}
                        onChange={e=>setProfileDraft({
                          ...profileDraft,
                          self_registration:e.target.checked,
                        })}
                      />
                      <span>Allow self registration</span>
                    </label>
                  </div>

                  <ConfirmAction
                    disabled={mutationBusy}
                    onClick={()=>void mutate(
                      "login-profile-update",
                      "Login profile updated",
                      hooks=>identityAccessApi.updateLoginProfile(
                        profileDraft.profile_key,
                        {
                          expected_version:profileDraft.version,
                          self_registration:profileDraft.self_registration,
                          min_assurance:profileDraft.min_assurance,
                          session_max_age_seconds:profileDraft.session_max_age_seconds,
                          default_role_key:profileDraft.default_role_key,
                          reason,
                        },
                        hooks,
                      ),
                    )}
                  >
                    Save profile with fresh passkey
                  </ConfirmAction>

                  {mutationNotice("login-profile-update","login-method-update")}

                  <div>
                    <SectionHeader
                      title="Authentication methods"
                      description="A planned provider cannot be enabled until the backend marks it LIVE."
                    />

                    <ResourceList label={`Authentication methods for ${profileDraft.display_name}`}>
                      <div className="iam-resource-list">
                        {profileDraft.methods.map(method=>
                          <div className="iam-resource-row" key={method.method_key}>
                            <div>
                              <strong>{method.method_key}</strong>
                              <span>
                                {method.enabled?"Enabled":"Disabled"} · {method.implementation_status}
                              </span>
                            </div>

                            <ConfirmAction
                              disabled={
                                mutationBusy
                                ||(!method.enabled&&method.implementation_status!=="LIVE")
                              }
                              onClick={()=>void mutate(
                                "login-method-update",
                                `${method.method_key} ${method.enabled?"disabled":"enabled"}`,
                                hooks=>identityAccessApi.updateLoginMethod(
                                  profileDraft.profile_key,
                                  method.method_key,
                                  !method.enabled,
                                  method.priority,
                                  reason,
                                  hooks,
                                ),
                              )}
                            >
                              {method.enabled?"Disable":"Enable"}
                            </ConfirmAction>
                          </div>
                        )}
                      </div>
                    </ResourceList>
                  </div>

                  <label className="iam-reason">
                    <span>Change reason</span>
                    <textarea
                      value={reason}
                      onChange={e=>setReason(e.target.value)}
                      maxLength={1000}
                      placeholder="Why is this authentication policy changing?"
                    />
                  </label>
                </div>
                :<EmptyState
                  title="Select a login profile"
                  detail="Authentication method and assurance policy appears here."
                />
              }
            </DetailPanel>
          </div>
        </section>}

        {view==="activity"&&<section className="iam-section">
          <SectionHeader
            title="Recent access activity"
            description="Human-readable activity is shown here while canonical atomic IAM events remain preserved."
          />

          {(control.recent_events??[]).length?
            <ResourceList label="Recent IAM events">
              <div className="iam-resource-list">
                {(control.recent_events??[]).map(event=>
                  <div className="iam-resource-row" key={String(event.id)}>
                    <div>
                      <strong>{event.event_type}</strong>
                      <span>{event.target_key||event.target_type||"IAM"} · {fmtDate(event.created_at)}</span>
                    </div>
                    <StateText tone={event.outcome==="APPLIED"?"positive":"neutral"}>
                      {event.outcome}
                    </StateText>
                  </div>
                )}
              </div>
            </ResourceList>
            :<EmptyState
              title="No IAM activity"
              detail="No recent IAM mutation events are available."
            />
          }
        </section>}
      </>
    }

    {!control&&cp.status==="success"&&
      <EmptyState
        title="Identity control plane unavailable"
        detail="The IAM endpoint returned no control-plane payload."
      />
    }
  </div></IdentityAccessTranslate>;
}
